import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { JwtPayload } from '../models';
import { decodeTokenPayload } from '../utils';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiUrl = environment.apiUrl;
  private readonly loginUrl = `${this.apiUrl}/usuarios/auth/login`;
  private readonly logoutUrl = `${this.apiUrl}/usuarios/auth/logout`;
  private readonly refreshTokenUrl = `${this.apiUrl}/usuarios/auth/refresh-token`;
  private readonly accessToken = 'AccessToken';

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router
  ) {}

  login(credentials: { correo: string; contrasena: string }): Observable<{ accessToken: string }> {
    return this.http
      .post<{
        accessToken: string;
      }>(this.loginUrl, credentials, { withCredentials: true })
      .pipe(
        tap((response: any) => {
          if (response?.accessToken) {
            this.setAccessToken(response.accessToken);
          }
        })
      );
  }

  logout(): Observable<void> {
    return new Observable((observer) => {
      this.http.post(this.logoutUrl, {}, { withCredentials: true, responseType: 'text' }).subscribe({
        next: () => {
          this.forceLogout();
          observer.next();
          observer.complete();
        },
        error: (error) => {
          console.error('Error during logout:', error);
          observer.error(error);
          this.forceLogout();
        },
      });
    });
  }

  forceLogout(): void {
    this.clearAccessToken();
    this.router.navigate(['/login']);
  }

  refreshAccessToken(): Observable<{ accessToken: string }> {
    return this.http
      .post<{
        accessToken: string;
      }>(this.refreshTokenUrl, {}, { withCredentials: true })
      .pipe(
        tap((response) => {
          if (response?.accessToken) {
            this.setAccessToken(response.accessToken);
          }
        }),
        catchError((error) => {
          console.error('Error refreshing token:', error);
          this.logout();
          return throwError(() => error);
        })
      );
  }

  // --- TOKEN ---
  setAccessToken(token: string): void {
    localStorage.setItem(this.accessToken, token);
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.accessToken);
  }

  clearAccessToken(): void {
    localStorage.removeItem(this.accessToken);
  }

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    return !!token && !this.isTokenExpired(token);
  }

  isTokenExpired(token: string): boolean {
    const payload = decodeTokenPayload(token);
    if (!payload?.exp) return true;

    const expirationDate = new Date(payload.exp * 1000);
    return expirationDate < new Date();
  }

  // --- CLAIMS ---
  getUserClaims(): JwtPayload | null {
    const token = this.getAccessToken();
    return token ? decodeTokenPayload(token) : null;
  }

  getUserName(): string | null {
    return this.getUserClaims()?.name ?? null;
  }

  getUserRoles(): string[] | null {
    return this.getUserClaims()?.role ?? null;
  }

  hasRole(role: string): boolean {
    return this.getUserRoles()?.includes(role) ?? false;
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }
}
